var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a;
import { VIDEO_EVTS, VIDEO_STATE } from '../enums';
import { Logger } from '../log';
import { logMethodCallDecoratorCreator, uaTool } from '../util';
/**
 * 当前状态的前置状态表，例如loadStart上一个状态表为[idle]
 */
export var VALID_PREVIOUS_STATE_MAP = (_a = {},
    _a[VIDEO_STATE.LOAD_START] = [VIDEO_STATE.IDLE],
    _a[VIDEO_STATE.CANPLAY] = [VIDEO_STATE.LOAD_START],
    _a[VIDEO_STATE.PLAYING] = [VIDEO_STATE.CANPLAY, VIDEO_STATE.SEEKED, VIDEO_STATE.PAUSE, VIDEO_STATE.BUFFERING],
    _a[VIDEO_STATE.BUFFERING] = [VIDEO_STATE.PLAYING],
    _a[VIDEO_STATE.PAUSE] = [VIDEO_STATE.PLAYING, VIDEO_STATE.BUFFERING],
    _a[VIDEO_STATE.SEEKING] = [
        VIDEO_STATE.CANPLAY, VIDEO_STATE.PLAYING, VIDEO_STATE.PAUSE, VIDEO_STATE.BUFFERING, VIDEO_STATE.SEEKING,
    ],
    _a[VIDEO_STATE.SEEKED] = [VIDEO_STATE.SEEKING],
    _a[VIDEO_STATE.END] = [
        VIDEO_STATE.LOAD_START,
        VIDEO_STATE.CANPLAY,
        VIDEO_STATE.SEEKING,
        VIDEO_STATE.SEEKED,
        VIDEO_STATE.BUFFERING,
        VIDEO_STATE.PLAYING,
        VIDEO_STATE.PAUSE,
    ],
    _a[VIDEO_STATE.IDLE] = [VIDEO_STATE.END],
    _a);
/**
 * 切清晰度时的合法状态。例如无缝切换时，实际上是可以进行pause等操作的。
 */
export var VALID_STATE_IN_LEVEL_SWITCHING = [
    VIDEO_STATE.PAUSE,
    VIDEO_STATE.PLAYING,
    VIDEO_STATE.END,
    VIDEO_STATE.IDLE,
];
var logMethodCall = logMethodCallDecoratorCreator('StateMgr');
/**
 * VideoNode的状态机管理，根据video事件转换为播放状态机。[状态图参考](document/state.png)
 */
var StateMgr = /** @class */ (function () {
    function StateMgr(vn) {
        var _a;
        this.videoEvtHandler = (_a = {},
            _a[VIDEO_EVTS.canplay] = this.onCanplayEvt.bind(this),
            _a[VIDEO_EVTS.playing] = this.setPlaying.bind(this),
            _a[VIDEO_EVTS.waiting] = this.setWaiting.bind(this),
            _a[VIDEO_EVTS.pause] = this.onPauseEvt.bind(this),
            _a[VIDEO_EVTS.seeking] = this.onSeekingEvt.bind(this),
            _a[VIDEO_EVTS.seeked] = this.onSeekedEvt.bind(this),
            _a);
        this.videoNode = vn;
        this.logger = new Logger(vn.config.instanceId, 'StateMgr');
    }
    StateMgr.prototype.destroy = function () {
        this.videoNode = null;
    };
    StateMgr.prototype.validCallState = function (tarState) {
        var _a;
        return (_a = VALID_PREVIOUS_STATE_MAP[tarState]) === null || _a === void 0 ? void 0 : _a.includes(this.videoNode.state);
    };
    StateMgr.prototype.setEnded = function () {
        this.setVideoNodeState(VIDEO_STATE.END);
        // end状态以后紧跟idle状态（历史原因导致这两个状态现在没有什么区别，但需要兼容老的调用方）
        this.setVideoNodeState(VIDEO_STATE.IDLE);
    };
    StateMgr.prototype.setLoadStart = function () {
        this.setVideoNodeState(VIDEO_STATE.LOAD_START);
    };
    StateMgr.prototype.setCanplay = function () {
        this.setVideoNodeState(VIDEO_STATE.CANPLAY);
    };
    StateMgr.prototype.setWaiting = function () {
        this.setVideoNodeState(VIDEO_STATE.BUFFERING);
    };
    StateMgr.prototype.setPlaying = function () {
        this.setVideoNodeState(VIDEO_STATE.PLAYING);
    };
    StateMgr.prototype.onSetLevelOver = function () {
        if (this.videoNode.state === VIDEO_STATE.PAUSE) {
            // 在暂停状态切换清晰度，切换成功以后是播放状态，因此需要修改为playing状态
            this.setVideoNodeState(VIDEO_STATE.PLAYING);
        }
    };
    /**
     * 收到video标签事件以后进行处理
     * @param evtName video标签事件名
     */
    StateMgr.prototype.receiveVideoEvt = function (evtName) {
        var _a, _b;
        (_b = (_a = this.videoEvtHandler)[evtName]) === null || _b === void 0 ? void 0 : _b.call(_a);
    };
    /**
     * 设置VideoNode的状态，此状态是排除非法状态、兼容性处理以后完全处理好的状态。
     * @param state 状态名
     */
    StateMgr.prototype.setVideoNodeState = function (state) {
        if (this.videoNode.getLevelSwitching() && !VALID_STATE_IN_LEVEL_SWITCHING.includes(state)) {
            // 切换清晰度时过滤一部分状态
            this.logger.log("can not set state 2 ".concat(state, " when level switching"));
            return;
        }
        if (!VALID_PREVIOUS_STATE_MAP[state].includes(this.videoNode.state)) {
            // 上一个状态必须符合前置状态表
            this.logger.log("can not set state from ".concat(this.videoNode.state, " to ").concat(state));
            return;
        }
        this.videoNode.state = state;
    };
    StateMgr.prototype.onCanplayEvt = function () {
        // ios的waiting以后是canplay和canplaythrough（没有playing事件）这里需要判断以后额外加一个事件。
        // 另外发现safari播放直播时，从静音到恢复音量，video的事件变化为playing->waiting->canplay，所以会丢失playing状态。
        var applePlat = uaTool.os.ios || uaTool.browser.safari;
        if (applePlat && this.isVideoElementPlaying() && this.videoNode.state === VIDEO_STATE.BUFFERING) {
            this.setVideoNodeState(VIDEO_STATE.PLAYING);
        }
        else {
            this.setVideoNodeState(VIDEO_STATE.CANPLAY);
        }
    };
    StateMgr.prototype.onPauseEvt = function () {
        this.setVideoNodeState(VIDEO_STATE.PAUSE);
    };
    StateMgr.prototype.onSeekingEvt = function () {
        this.setVideoNodeState(VIDEO_STATE.SEEKING);
    };
    StateMgr.prototype.onSeekedEvt = function () {
        this.setVideoNodeState(VIDEO_STATE.SEEKED);
        var applePlat = uaTool.os.ios || uaTool.browser.safari;
        if (applePlat && this.isVideoElementPlaying()) {
            // 播放中seek，ios的seeked事件以后不是playing状态。这里需要额外增加playing状态
            this.setVideoNodeState(VIDEO_STATE.PLAYING);
        }
    };
    /**
     * video标签是否在播放状态
     */
    StateMgr.prototype.isVideoElementPlaying = function () {
        return !this.videoNode.videoElement.paused && !this.videoNode.videoElement.ended;
    };
    __decorate([
        logMethodCall,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], StateMgr.prototype, "destroy", null);
    __decorate([
        logMethodCall,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String]),
        __metadata("design:returntype", void 0)
    ], StateMgr.prototype, "validCallState", null);
    __decorate([
        logMethodCall,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], StateMgr.prototype, "setEnded", null);
    __decorate([
        logMethodCall,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], StateMgr.prototype, "setLoadStart", null);
    __decorate([
        logMethodCall,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], StateMgr.prototype, "setCanplay", null);
    __decorate([
        logMethodCall,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], StateMgr.prototype, "setWaiting", null);
    __decorate([
        logMethodCall,
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], StateMgr.prototype, "setPlaying", null);
    return StateMgr;
}());
export { StateMgr };
